import useInfiniteScroll from "react-infinite-scroll-hook";
import { useIntl } from "react-intl";

import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { BookingsEmptyState } from "@/features/bookings/components/BookingsEmptyState";
import { OrderCardWithoutReservation } from "@/features/bookings/components/OrderCardWithoutReservation";
import { OrdersInCreationCard } from "@/features/bookings/components/OrdersInCreationCard";
import { useReservations } from "@/features/bookings/hooks/useReservations";
import { ReservationType } from "@/features/bookings/types/types";
import { SingleAvailableFlightReservation } from "@/features/flights/components/SingleAvailableFlightReservation";
import { formatCurrency } from "@/utils/currency";

export const MyFlightsList = ({
  onReservationClick,
  reservationType,
  reservationTypeSwitcher,
}: {
  onReservationClick: (reservationId: string) => void;
  reservationType: ReservationType;
  reservationTypeSwitcher: React.ReactNode;
}) => {
  const intl = useIntl();
  const { data, isLoading, isFetching, isError, hasReachedEnd, fetchNextPage } =
    useReservations({ searchFilter: reservationType, service: "air" });

  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: !hasReachedEnd,
    onLoadMore: () => {
      fetchNextPage();
    },
    disabled: isError,
    rootMargin: "0px 0px 40px 0px",
  });

  if (isLoading) {
    return (
      <PageLoader>
        <AnimatedLoader icon="airplane" />
      </PageLoader>
    );
  }

  const flightCount = data?.pages?.[0]?.data?.count;
  const bookings =
    data?.pages?.flatMap((page) => page.data.flightReservations) || [];
  const inProgressReservationsCount =
    data?.pages?.[0]?.data?.inProgressReservationsCount;

  const hasInProgressOrders =
    reservationType === ReservationType.UPCOMING &&
    !!inProgressReservationsCount;

  if (
    !isFetching &&
    hasReachedEnd &&
    !isError &&
    bookings.length === 0 &&
    !hasInProgressOrders
  ) {
    return (
      <>
        <div className="mb-8 flex items-center justify-between">
          {reservationTypeSwitcher}
        </div>
        <BookingsEmptyState type="flights" reservationType={reservationType} />
      </>
    );
  }

  return (
    <>
      <div className="mb-8 flex items-center justify-between">
        {reservationTypeSwitcher}
        {flightCount ? (
          <div className="text-md font-medium">
            {intl.formatMessage(
              {
                defaultMessage:
                  "{flightCount, plural, one {# trip} other {# trips}}",
                description: "Count of booked flights shown in My Bookings",
              },
              { flightCount },
            )}
          </div>
        ) : null}
      </div>

      {inProgressReservationsCount && inProgressReservationsCount > 0 ? (
        <OrdersInCreationCard count={inProgressReservationsCount} />
      ) : null}

      {bookings.length > 0 ? (
        <ul
          aria-label={intl.formatMessage({
            defaultMessage: "Booked flights list",
            description: "Accessible heading for the list of booked flights",
          })}
          className="space-y-2"
        >
          {bookings.map((reservation) => (
            <li key={reservation.id}>
              {reservation.status === ReservationType.REFUNDED ? (
                <OrderCardWithoutReservation
                  reservation={reservation}
                  onClick={() => onReservationClick(reservation.id)}
                />
              ) : (
                <SingleAvailableFlightReservation
                  onClick={() => onReservationClick(reservation.id)}
                  itinerary={reservation.externalReservation}
                  reservationStatus={reservation.status}
                  bottomInfo={
                    <div className="text-end text-sm font-semibold text-text-primary">
                      {formatCurrency(reservation.price, reservation.currency)}
                    </div>
                  }
                />
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {isFetching || !hasReachedEnd ? (
        <div
          className="flex items-center justify-center py-4"
          ref={sentryRef}
          aria-hidden={!isFetching}
        >
          <AnimatedLoader icon="airplane" />
        </div>
      ) : (
        <div className="flex items-center justify-center p-8 text-text-tertiary">
          {intl.formatMessage({
            defaultMessage: "No more results",
            description:
              "End-of-list message when there are no more results to load",
          })}
        </div>
      )}
    </>
  );
};
